/**
 * Checklist Template Service
 *
 * CRUD operations for the checklist *template* CMS surface — Super
 * Admin only. Templates are reusable checklist definitions that get
 * snapshotted onto Work Orders or Inspections at assignment time.
 *
 * Versioning behavior (server-side):
 *   - update() creates a NEW version under the hood; the prior
 *     version is automatically marked is_active=false. Old completed
 *     work keeps its frozen snapshot.
 *   - updateVisibility() updates pivots in place — does NOT version.
 *
 * Backend endpoints (under /api/v1):
 *   GET    /checklists                    — list (super_admin)
 *   POST   /checklists                    — create new template
 *   GET    /checklists/{id}               — show
 *   PATCH  /checklists/{id}               — update (creates new version)
 *   POST   /checklists/{id}/visibility    — update pivots only
 *   POST   /checklists/{id}/duplicate     — duplicate as fresh v1
 *   DELETE /checklists/{id}               — soft delete
 *   GET    /checklists/assignable         — visible to current user
 *
 * All endpoints require Sanctum auth.
 */

import { api } from './api';

/* ============================================
 * TYPES
 * ============================================ */

/**
 * A single template item — the building block of a checklist. When
 * a template is assigned to a WO/Inspection, each item is snapshotted
 * into a ChecklistItem row on the resulting instance.
 */
export interface ChecklistTemplateItem {
    id: string;
    checklistId: string;
    title: string;
    details: string | null;
    additionalDetails: string | null;
    /**
     * Photo capture mode — drives the field UX. See
     * `ChecklistItemPhotoMode` in checklistService.ts for the
     * semantics ('none' | 'photo' | 'before_after').
     */
    photoMode: 'none' | 'photo' | 'before_after';
    /** When true, the configured photo(s) must be attached to mark
     *  the snapshotted instance item complete. Ignored when mode='none'. */
    photoRequired: boolean;
    isRequired: boolean;
    sortOrder: number;
    createdAt?: string;
    updatedAt?: string;
}

/**
 * Visibility pivot rows surfaced on the response.
 */
export interface ChecklistVisibilityCompany {
    companyId: string;
    name: string;
}

export interface ChecklistVisibilityUser {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
}

/**
 * Template kind. Templates are authored once in the CMS but render
 * differently in the field depending on this discriminator:
 *   - work_order: per-item checkbox (binary completion).
 *   - inspection: per-item Good / Needs Attention / Critical rating
 *     with a required note when Critical.
 * Also drives JobForm filtering — a Work Order's template dropdown
 * only shows 'work_order' templates and an Inspection's only shows
 * 'inspection' templates.
 */
export type ChecklistTemplateType = 'work_order' | 'inspection';

/**
 * A checklist template as returned by the API.
 *
 * @property version            — monotonic version number within the chain
 * @property parentChecklistId  — points at the prior version (null for v1)
 * @property isActive           — false when superseded by a newer version
 */
export interface ChecklistTemplate {
    id: string;
    title: string;
    description: string | null;
    /** Discriminator — defaults to 'work_order' for older rows. */
    type: ChecklistTemplateType;
    isVisibleToAll: boolean;
    isActive: boolean;
    parentChecklistId: string | null;
    version: number;
    createdBy: string | null;
    items: ChecklistTemplateItem[];
    companies?: ChecklistVisibilityCompany[];
    users?: ChecklistVisibilityUser[];
    creator?: { id: string; firstName: string; lastName: string };
    /** Business Lines whose line-level default is this checklist. The
     *  CMS surfaces at most one entry; modelled as an array because
     *  the relation is HasMany on the backend. */
    defaultForBusinessLines?: Array<{ id: number; slug: string; label: string }>;
    /** Sub-types whose default is this checklist. Multi-select on edit. */
    defaultForSubTypes?: Array<{ id: number; businessLineId: number; slug: string; label: string }>;
    itemsCount?: number;
    instancesCount?: number;
    createdAt: string;
    updatedAt: string;
}

/**
 * Item shape accepted in create/update payloads. The id is omitted
 * because the server always re-creates items on save (full sync).
 */
export interface ChecklistTemplateItemPayload {
    title: string;
    details?: string | null;
    additionalDetails?: string | null;
    /** 'none' | 'photo' | 'before_after' — defaults to 'none' on the server. */
    photoMode?: 'none' | 'photo' | 'before_after';
    /** Defaults to false on the server. */
    photoRequired?: boolean;
    isRequired?: boolean;
    sortOrder?: number;
}

/**
 * Payload for creating or updating a checklist template.
 *
 * Notes:
 *   - companyIds and userIds are full replacements when present.
 *     Omit the keys to leave the existing pivots untouched.
 *   - On update, providing items will REPLACE the entire item list
 *     (the server deletes existing items and inserts the supplied set).
 */
export interface ChecklistTemplatePayload {
    title: string;
    description?: string | null;
    /** Defaults to 'work_order' on the server when omitted. */
    type?: ChecklistTemplateType;
    isVisibleToAll?: boolean;
    companyIds?: number[];
    userIds?: number[];
    items?: ChecklistTemplateItemPayload[];
    /** CMS-managed default-checklist association. When defaultSubTypeIds
     *  is non-empty, those sub-types claim this checklist as their default
     *  and the line-level slot stays unused. Omit both keys to leave
     *  existing defaults untouched. */
    defaultBusinessLineId?: number | null;
    defaultSubTypeIds?: number[];
}

/**
 * Visibility-only update payload — does NOT version the template.
 */
export interface ChecklistVisibilityPayload {
    isVisibleToAll?: boolean;
    companyIds?: number[];
    userIds?: number[];
}

/**
 * The list endpoint returns Laravel paginated data.
 */
export interface PaginatedChecklists {
    data: ChecklistTemplate[];
    meta?: {
        currentPage: number;
        perPage: number;
        total: number;
        lastPage?: number;
    };
    links?: Record<string, string | null>;
}

/* ============================================
 * SERVICE
 * ============================================ */

export const checklistTemplateService = {
    /** GET /checklists — paginated list for the CMS table */
    list: (params?: Record<string, string>) =>
        api.get<PaginatedChecklists>('/checklists', params),

    /** GET /checklists/{id} — full detail with items + visibility pivots */
    getById: (id: string) =>
        api.get<{ data: ChecklistTemplate }>(`/checklists/${id}`),

    /** POST /checklists — create new template (version 1) */
    create: (payload: ChecklistTemplatePayload) =>
        api.post<{ data: ChecklistTemplate }>('/checklists', payload),

    /** PATCH /checklists/{id} — content edit; server creates a new version */
    update: (id: string, payload: ChecklistTemplatePayload) =>
        api.patch<{ data: ChecklistTemplate }>(`/checklists/${id}`, payload),

    /** POST /checklists/{id}/visibility — pivot-only update, no versioning */
    updateVisibility: (id: string, payload: ChecklistVisibilityPayload) =>
        api.post<{ data: ChecklistTemplate }>(`/checklists/${id}/visibility`, payload),

    /** POST /checklists/{id}/duplicate — clone as fresh v1 */
    duplicate: (id: string) =>
        api.post<{ data: ChecklistTemplate }>(`/checklists/${id}/duplicate`),

    /** DELETE /checklists/{id} — soft delete */
    delete: (id: string) =>
        api.delete<void>(`/checklists/${id}`),

    /**
     * GET /checklists/assignable — list visible to the current user.
     * Used by the WO/Inspection assignment dropdown (open to any auth).
     *
     * Pass `type` to scope the result so the WO form's dropdown only
     * shows 'work_order' templates and the Inspection form's dropdown
     * only shows 'inspection' templates. Omit it to receive all types
     * (used by callers that don't need to narrow, e.g. the CMS browser).
     */
    assignable: (type?: ChecklistTemplateType) => {
        const params = type ? { type } : undefined;
        return api.get<{ data: ChecklistTemplate[] }>('/checklists/assignable', params);
    },
};
